import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import LockedGate from "@/components/LockedGate";
import MiniChart, { normalizeSymbol } from "@/components/MiniChart";
import TradeJournal from "@/components/TradeJournal";
import MasterTraders from "@/components/MasterTraders";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, XCircle, Radio, Percent, CheckCircle2, BookMarked, Star } from "lucide-react";

function Stat({ label, value, tone, icon: Icon }) {
  const toneCls =
    tone === "emerald" ? "text-emerald-700" :
    tone === "rose" ? "text-rose-700" :
    tone === "brand" ? "text-[#00b4ff]" : "text-slate-900";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono-plex font-semibold tracking-widest uppercase text-slate-700">{label}</div>
        {Icon && <Icon size={14} className="text-slate-300" />}
      </div>
      <div className={`mt-2 font-display font-bold text-3xl tracking-tight ${toneCls}`}>{value}</div>
    </div>
  );
}

function SignalCard({ s }) {
  const dirColor = s.direction === "buy" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-rose-700 bg-rose-50 border-rose-200";
  const statusMeta = {
    active:    { label: "ACTIVE",    cls: "bg-[#00b4ff] text-white", Icon: Radio, live: true },
    hit_tp:    { label: "HIT TP",    cls: "bg-emerald-600 text-white", Icon: CheckCircle2 },
    hit_sl:    { label: "HIT SL",    cls: "bg-rose-600 text-white", Icon: XCircle },
    cancelled: { label: "CANCELLED", cls: "bg-slate-500 text-white", Icon: XCircle },
  }[s.status] || { label: s.status, cls: "bg-slate-500 text-white", Icon: Radio };

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white" data-testid={`perf-signal-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-bold text-2xl tracking-tight">{s.pair}</div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase border ${dirColor}`}>
              {s.direction === "buy" ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {s.direction}
            </span>
          </div>
          {s.note && <div className="mt-1 text-sm text-slate-600 truncate">{s.note}</div>}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono-plex tracking-widest ${statusMeta.cls}`}>
          {statusMeta.live && <span className="live-dot" />}
          <statusMeta.Icon size={11} /> {statusMeta.label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-slate-100">
        <div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">Entry</div>
          <div className="mt-1 font-mono-plex text-sm font-semibold">{s.entry ?? "—"}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">TP</div>
          <div className="mt-1 font-mono-plex text-sm font-semibold text-emerald-700">{s.tp}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">SL</div>
          <div className="mt-1 font-mono-plex text-sm font-semibold text-rose-700">{s.sl}</div>
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50/60 overflow-hidden">
        <MiniChart symbol={normalizeSymbol(s.pair)} height={130} />
      </div>
      <div className="mt-3 text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">
        {new Date(s.created_at).toLocaleString()}
      </div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-slate-500 text-sm">{children}</div>;
}

function SignalsHistory() {
  const [signals, setSignals] = useState([]);
  const [filter, setFilter] = useState("complete");

  const load = async () => { try { const { data } = await api.get("/signals"); setSignals(data); } catch (err) { logDev(err); } };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const summary = useMemo(() => {
    const tp = signals.filter((s) => s.status === "hit_tp").length;
    const sl = signals.filter((s) => s.status === "hit_sl").length;
    const cx = signals.filter((s) => s.status === "cancelled").length;
    const active = signals.filter((s) => s.status === "active").length;
    const complete = tp + sl + cx;
    const decided = tp + sl;
    const rate = decided > 0 ? Math.round((tp / decided) * 100) : 0;
    return { tp, sl, cx, active, complete, decided, total: signals.length, rate };
  }, [signals]);

  const filtered = useMemo(() => {
    if (filter === "complete") return signals.filter((s) => s.status !== "active");
    return signals.filter((s) => s.status === filter);
  }, [signals, filter]);

  const filters = [
    { key: "active",    label: `Active (${summary.active})` },
    { key: "complete",  label: `Complete (${summary.complete})` },
    { key: "hit_tp",    label: `Hit TP (${summary.tp})` },
    { key: "hit_sl",    label: `Hit SL (${summary.sl})` },
    { key: "cancelled", label: `Cancelled (${summary.cx})` },
  ];

  return (
    <div>
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="Total Signals" value={summary.total} icon={Radio} />
        <Stat label="Active Now" value={summary.active} tone="brand" icon={Radio} />
        <Stat label="Hit TP" value={summary.tp} tone="emerald" icon={TrendingUp} />
        <Stat label="Hit SL" value={summary.sl} tone="rose" icon={TrendingDown} />
        <Stat label="Win Rate" value={`${summary.rate}%`} tone="brand" icon={Percent} />
      </div>

      {/* Win-rate bar */}
      {summary.decided > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">Signal accuracy · TP vs SL</div>
              <div className="mt-1 font-display text-lg font-semibold tracking-tight">
                {summary.tp} wins / {summary.sl} losses
              </div>
            </div>
            <div className="font-mono-plex text-2xl font-bold text-[#00b4ff]">{summary.rate}%</div>
          </div>
          <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden flex">
            <div className="h-full bg-emerald-500" style={{ width: `${summary.rate}%`, transition: "width 0.4s ease" }} />
            <div className="h-full bg-rose-500" style={{ width: `${100 - summary.rate}%`, transition: "width 0.4s ease" }} />
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={`perf-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase border ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            style={{ transition: "background-color 0.2s ease" }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Empty>Tiada signal untuk filter ini.</Empty>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((s, i) => (
            <div key={s.id} className="stagger-item" style={{ animationDelay: `${i * 40}ms` }}>
              <SignalCard s={s} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PerformancePage() {
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-16">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-[#00b4ff]">Analytics</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight">Performance</h1>
        <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">
          Sangat sesuai untuk track record Trading sendiri.
        </p>
      </div>

      <div className="mt-10">
        <LockedGate feature="Performance">
          <Tabs defaultValue="journal" className="w-full">
            <TabsList className="bg-slate-100 p-1 rounded-full max-w-full overflow-x-auto justify-start no-scrollbar">
              <TabsTrigger data-testid="tab-perf-journal" value="journal" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow font-display font-semibold text-sm">
                <BookMarked size={14} className="mr-2" /> Trade Journal
              </TabsTrigger>
              <TabsTrigger data-testid="tab-perf-signals" value="signals" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow font-display font-semibold text-sm">
                <Radio size={14} className="mr-2" /> Signals
              </TabsTrigger>
              <TabsTrigger data-testid="tab-perf-masters" value="masters" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow font-display font-semibold text-sm">
                <Star size={14} className="mr-2" /> Master Traders
              </TabsTrigger>
            </TabsList>
            <TabsContent value="journal" className="mt-6"><TradeJournal /></TabsContent>
            <TabsContent value="signals" className="mt-6"><SignalsHistory /></TabsContent>
            <TabsContent value="masters" className="mt-6"><MasterTraders /></TabsContent>
          </Tabs>
        </LockedGate>
      </div>
    </div>
  );
}
